import React, { createContext, useContext, useState, useEffect } from 'react';

const GlobalConfigContext = createContext();

// Per-WC "include in Optimize All" selection, stored as { wcLabel: bool }.
// Exported as plain functions as well as through the context because
// WorkflowCanvas RENDERS the provider and so cannot consume it — and its own
// sidebar "Run Optimization" must honour the same ticks. One definition, read
// both ways, so the two optimize paths can never disagree.
export const WC_OPTIMIZE_KEY = 'wc-optimize-enabled';

export const readWcOptimizeEnabled = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(WC_OPTIMIZE_KEY) || '{}');
    return (saved && typeof saved === 'object') ? saved : {};
  } catch (e) {
    return {};
  }
};

// A work center NOT in the map counts as ticked, so nothing silently drops out
// of an optimization — only an explicit untick removes one.
export const isWcOptimizeEnabledFor = (map, wcLabel) => (map || {})[wcLabel] !== false;

export const useGlobalConfig = () => {
  const context = useContext(GlobalConfigContext);
  if (!context) {
    throw new Error('useGlobalConfig must be used within GlobalConfigProvider');
  }
  return context;
};

export const GlobalConfigProvider = ({ children }) => {
  const [globalConfig, setGlobalConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Per-WC start date registry: { wcLabel: dateString }
  // MachineNodes register their local scheduleStartDate here so
  // DispatcherNode can read them when "Optimize All" is clicked.
  const [wcStartDates, setWcStartDates] = useState({});
  const registerWcStartDate = (wcLabel, dateStr) => {
    setWcStartDates(prev => {
      if (prev[wcLabel] === dateStr) return prev;
      return { ...prev, [wcLabel]: dateStr };
    });
  };

  // Per-WC "include in Optimize All" registry (see the module-level helpers
  // above). Set by the checkbox in each MachineNode's corner, read by
  // DispatcherNode when the common optimize button is pressed.
  const [wcOptimizeEnabled, setWcOptimizeEnabled] = useState(readWcOptimizeEnabled);

  const setWcOptimize = (wcLabel, enabled) => {
    setWcOptimizeEnabled(prev => {
      if (prev[wcLabel] === enabled) return prev;
      const next = { ...prev, [wcLabel]: enabled };
      try {
        localStorage.setItem(WC_OPTIMIZE_KEY, JSON.stringify(next));
      } catch (e) { /* a full/blocked localStorage must not break the click */ }
      return next;
    });
  };

  // The question every caller actually asks. Default true (see above).
  const isWcOptimizeEnabled = (wcLabel) => isWcOptimizeEnabledFor(wcOptimizeEnabled, wcLabel);

  // Load initial config from backend on mount
  useEffect(() => {
    const loadInitialConfig = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/api/config/optimization`);
        
        if (response.ok) {
          const result = await response.json();
          console.log('🔍 API response for optimization config:', result);
          
          if (result.success && result.config) {
            // Prefer YAML values for most settings, but preserve capacity info
            // AND user-changed settings from localStorage (survives page refresh / dataset reload)
            let savedTimeLimit = null;
            let savedBatchSize = null;
            let savedScheduleStartDate = null;
            let savedCapacityFileName = '';
            try {
              const saved = JSON.parse(localStorage.getItem('global-optimization-config') || '{}');
              savedTimeLimit = saved.timeLimit ?? null;
              savedBatchSize = saved.batchSize ?? null;
              savedScheduleStartDate = saved.scheduleStartDate ?? null;
              savedCapacityFileName = saved.capacityFileName || '';
            } catch (e) { /* ignore parse errors */ }
            
            const initialConfig = {
              timeLimit: savedTimeLimit ?? result.config.timeLimitMinutes ?? 5,
              batchSize: savedBatchSize ?? result.config.maxJobs ?? 40,
              capacityLoaded: result.config.capacityLoaded || false,
              capacityFileName: savedCapacityFileName,
              scheduleStartDate: savedScheduleStartDate ?? result.config.scheduleStartDateMode ?? 'next_available_shift',
              lastUpdated: Date.now()
            };
            
            console.log('✅ Global config initialized from YAML:', initialConfig);
            setGlobalConfig(initialConfig);
            localStorage.setItem('global-optimization-config', JSON.stringify(initialConfig));
          }
        }
      } catch (error) {
        console.error('Failed to load initial config from backend:', error);
        // Fallback to defaults if API fails
        const fallbackConfig = {
          timeLimit: 5,
          batchSize: 40,
          capacityLoaded: false,
          scheduleStartDate: 'next_available_shift',
          lastUpdated: Date.now()
        };
        setGlobalConfig(fallbackConfig);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadInitialConfig();
  }, []);

  // Persist to localStorage whenever config changes
  useEffect(() => {
    if (globalConfig) {
      localStorage.setItem('global-optimization-config', JSON.stringify(globalConfig));
    }
  }, [globalConfig]);

  const updateGlobalConfig = (updates) => {
    // When a global setting changes, clear per-WC localStorage overrides
    // so every MachineNode picks up the new global value.
    const keyMap = {
      timeLimit: 'timeLimit',
      batchSize: 'batchSize',
      scheduleStartDate: 'scheduleStartDate'
    };
    for (const prop of Object.keys(updates)) {
      const suffix = keyMap[prop];
      if (suffix) {
        // Remove all wc-*-<suffix> entries
        const toRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith('wc-') && k.endsWith(`-${suffix}`)) {
            toRemove.push(k);
          }
        }
        toRemove.forEach(k => localStorage.removeItem(k));
      }
    }

    setGlobalConfig(prev => ({
      ...prev,
      ...updates,
      lastUpdated: Date.now()
    }));
  };

  // Don't render children until config is loaded
  if (isLoading || !globalConfig) {
    return null;
  }

  const value = {
    globalConfig,
    updateGlobalConfig,
    wcStartDates,
    registerWcStartDate,
    wcOptimizeEnabled,
    isWcOptimizeEnabled,
    setWcOptimize
  };

  return (
    <GlobalConfigContext.Provider value={value}>
      {children}
    </GlobalConfigContext.Provider>
  );
};
